/*
 * API service for user login
 * Uses Firebase client SDK for user authentication
 */

import { auth, facebookProvider, githubProvider, googleProvider } from '../Firebase.js';
import { signInWithEmailAndPassword, signInWithPopup } from 'firebase/auth';
import apiClient from './api.js';

// Login user with Firebase REST API and backend for user profile
export async function login(email, password) {
  try {
    // Basic validation
    const validation = validateLoginData({ email, password });
    if (!validation.isValid) {
      throw new Error(Object.values(validation.errors)[0]);
    }

    // Execute reCAPTCHA for login action
    let recaptchaToken;
    try {
      recaptchaToken = await new Promise((resolve, reject) => {
        grecaptcha.enterprise.ready(async () => {
          const token = await grecaptcha.enterprise.execute('6Lf1JPsrAAAAANMbMPfk9QwitzOdzPwI8CLJUWcP', {action: 'LOGIN'});
          resolve(token);
        });
      });
    } catch (recaptchaError) {
      console.warn('reCAPTCHA failed, proceeding without token:', recaptchaError);
      recaptchaToken = null;
    }

    // Check if backend is in mock mode by making a test request
    let isMockMode = false;
    try {
      const testResponse = await fetch(`${apiClient.defaults.baseURL}/api/auth/test`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        signal: AbortSignal.timeout(5000), // 5 second timeout for test request
      });
      if (testResponse.ok) {
        const testData = await testResponse.json();
        isMockMode = testData.firebase === 'Firebase Admin not available' || testData.message?.includes('mock');
      }
    } catch (testError) {
      console.warn('Could not determine backend mode, assuming production mode:', testError.message);
    }

    let idToken, localId, firebaseEmail, displayName, emailVerified;

    if (isMockMode) {
      console.log('🔧 Backend is in mock mode, using mock authentication');
      // In mock mode, skip Firebase REST API and use mock credentials
      idToken = 'mock-jwt-token-' + Date.now();
      localId = 'Wc1q6AqKhnY4OKwnqLp57y7XaOy2'; // Use the mock user ID
      firebaseEmail = email;
      displayName = email.split('@')[0];
      emailVerified = true;
    } else {
      // Step 1: Authenticate with Firebase REST API
      const firebaseResponse = await fetch(`https://identitytoolkit.googleapis.com/v1/accounts:signInWithPassword?key=AIzaSyC2SdrHB3gf1nlXsuK2Sdg4h8HlNTbvnl4`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: email,
          password: password,
          returnSecureToken: true,
        }),
      });

      const firebaseData = await firebaseResponse.json();

      if (!firebaseResponse.ok) {
        // Handle Firebase REST API errors
        const errorMessage = firebaseData.error?.message;
        switch (errorMessage) {
          case 'EMAIL_NOT_FOUND':
            throw new Error('No account found with this email address. Please register first.');
          case 'INVALID_PASSWORD':
          case 'INVALID_LOGIN_CREDENTIALS':
            throw new Error('Incorrect password. Please try again.');
          case 'INVALID_EMAIL':
            throw new Error('Please enter a valid email address.');
          case 'USER_DISABLED':
            throw new Error('This account has been disabled.');
          case 'TOO_MANY_ATTEMPTS_TRY_LATER':
            throw new Error('Too many failed login attempts. Please try again later.');
          default:
            throw new Error('Login failed. Please try again.');
        }
      }

      // Get Firebase ID token from response
      idToken = firebaseData.idToken;
      localId = firebaseData.localId;
      firebaseEmail = firebaseData.email;
      displayName = firebaseData.displayName;
      emailVerified = firebaseData.emailVerified;

      // Store firebaseData for potential token refresh
      global.firebaseData = firebaseData;
    }

    // Step 2: Send Firebase ID token to backend for user profile and JWT
    let backendResponse;
    try {
      backendResponse = await apiClient.post('/api/auth/login-firebase', {
        idToken: idToken,
        localId: localId,
        email: firebaseEmail,
        displayName: displayName,
        emailVerified: emailVerified
      }, {
        headers: {
          Authorization: `Bearer ${idToken}`,
          'X-Override-Auth': 'true' // Flag to bypass interceptor
        }
      });
    } catch (backendError) {
      // Check if it's a 401 error
      if (backendError.response && backendError.response.status === 401) {
        const errorMessage = backendError.response.data?.message || '';

        // Check if backend is in mock mode
        if (errorMessage.includes('mock') || errorMessage.includes('Mock') ||
            errorMessage.includes('development') || errorMessage.includes('placeholder')) {
          console.warn('Backend appears to be in mock mode. Using mock user data for development.');
          // Return mock user data for development
          return {
            token: 'mock-jwt-token-' + Date.now(),
            user: {
              id: localId || 'mock-user-id',
              email: firebaseEmail,
              displayName: displayName || firebaseEmail.split('@')[0],
              emailVerified: emailVerified || false,
              planStatus: 'free',
              role: 'user',
              privateCode: 'MOCK' + Math.random().toString(36).substring(2, 8).toUpperCase(),
              createdAt: new Date().toISOString(),
              updatedAt: new Date().toISOString()
            }
          };
        }

        // If token is expired or invalid, refresh and retry
        console.log('Token expired, refreshing and retrying...');
        // For REST API, we need to refresh the token
        const refreshResponse = await fetch(`https://securetoken.googleapis.com/v1/token?key=AIzaSyC2SdrHB3gf1nlXsuK2Sdg4h8HlNTbvnl4`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            grant_type: 'refresh_token',
            refresh_token: global.firebaseData.refreshToken,
          }),
        });

        const refreshData = await refreshResponse.json();
        if (refreshResponse.ok) {
          const freshIdToken = refreshData.id_token;
          backendResponse = await apiClient.post('/api/auth/login-firebase', {
            idToken: freshIdToken,
            localId: global.firebaseData.localId,
            email: global.firebaseData.email,
            displayName: global.firebaseData.displayName,
            emailVerified: global.firebaseData.emailVerified
          }, {
            headers: {
              Authorization: `Bearer ${freshIdToken}`,
              'X-Override-Auth': 'true' // Flag to bypass interceptor
            }
          });
        } else {
          throw backendError;
        }
      } else {
        throw backendError;
      }
    }

    // Return backend response with JWT token and user profile including privateCode
    return backendResponse.data;
  } catch (error) {
    // Handle backend API errors
    if (error.response && error.response.config.url.includes('/api/auth/login-firebase')) {
      const errorMessage = error.response.data.message || 'Login failed';
      // Throw error with backend message to surface specific errors
      throw new Error(errorMessage);
    }

    // Network or other errors
    throw new Error(error.message || 'Login failed. Please try again.');
  }
}

// Login user with Facebook using Firebase client SDK
export async function loginWithFacebook() {
  try {
    const result = await signInWithPopup(auth, facebookProvider);
    const user = result.user;

    // Get Firebase ID token
    const idToken = await user.getIdToken();

    // Send the Firebase ID token to backend for authentication
    let response;
    try {
      response = await apiClient.post('/api/auth/login-firebase', {
        idToken: idToken,
        uid: user.uid,
        email: user.email,
        displayName: user.displayName,
        emailVerified: user.emailVerified
      }, {
        headers: {
          Authorization: `Bearer ${idToken}`,
          'X-Override-Auth': 'true' // Flag to bypass interceptor
        }
      });
    } catch (backendError) {
      // Check if it's a 401 error
      if (backendError.response && backendError.response.status === 401) {
        const errorMessage = backendError.response.data?.message || '';

        // Check if backend is in mock mode
        if (errorMessage.includes('mock') || errorMessage.includes('Mock') ||
            errorMessage.includes('development') || errorMessage.includes('placeholder')) {
          console.warn('Backend appears to be in mock mode. Using mock user data for development.');
          // Return mock user data for development
          return {
            token: 'mock-jwt-token-' + Date.now(),
            user: {
              id: user.uid,
              email: user.email,
              displayName: user.displayName || user.email.split('@')[0],
              emailVerified: user.emailVerified || false,
              planStatus: 'free',
              role: 'user',
              privateCode: 'MOCK' + Math.random().toString(36).substring(2, 8).toUpperCase(),
              createdAt: new Date().toISOString(),
              updatedAt: new Date().toISOString()
            }
          };
        }

        // If token is expired or invalid, refresh and retry
        console.log('Token expired, refreshing and retrying...');
        const freshIdToken = await user.getIdToken(true); // Force refresh
        response = await apiClient.post('/api/auth/login-firebase', {
          idToken: freshIdToken,
          uid: user.uid,
          email: user.email,
          displayName: user.displayName,
          emailVerified: user.emailVerified
        }, {
          headers: {
            Authorization: `Bearer ${freshIdToken}`,
            'X-Override-Auth': 'true' // Flag to bypass interceptor
          }
        });
      } else {
        throw backendError;
      }
    }

    return {
      token: response.data.token,
      user: response.data.user
    };
  } catch (error) {
    // Handle errors here
    const errorCode = error.code;
    const errorMessage = error.message;
    const email = error.customData?.email;
    const credential = facebookProvider.credentialFromError(error);

    throw new Error(errorMessage || 'Facebook login failed');
  }
}

// Validate login form data (similar to register validation)
export const validateLoginData = (loginData) => {
  const { email, password } = loginData;
  const errors = {};

  // Required field validation
  if (!email?.trim()) errors.email = 'Email is required';
  if (!password) errors.password = 'Password is required';

  // Email format validation
  if (email && !/\S+@\S+\.\S+/.test(email)) {
    errors.email = 'Please enter a valid email address';
  }

  // Password basic validation
  if (password && password.length < 6) {
    errors.password = 'Password must be at least 6 characters';
  }

  return {
    isValid: Object.keys(errors).length === 0,
    errors
  };
};

// Login user with GitHub using Firebase client SDK
export async function loginWithGithub() {
  try {
    const result = await signInWithPopup(auth, githubProvider);
    const user = result.user;

    // Get Firebase ID token
    const idToken = await user.getIdToken();

    // Send the Firebase ID token to backend for authentication
    let response;
    try {
      response = await apiClient.post('/api/auth/login-firebase', {
        idToken: idToken,
        uid: user.uid,
        email: user.email,
        displayName: user.displayName,
        emailVerified: user.emailVerified
      }, {
        headers: {
          Authorization: `Bearer ${idToken}`,
          'X-Override-Auth': 'true' // Flag to bypass interceptor
        }
      });
    } catch (backendError) {
      // Check if it's a 401 error
      if (backendError.response && backendError.response.status === 401) {
        const errorMessage = backendError.response.data?.message || '';

        // Check if backend is in mock mode
        if (errorMessage.includes('mock') || errorMessage.includes('Mock') ||
            errorMessage.includes('development') || errorMessage.includes('placeholder')) {
          console.warn('Backend appears to be in mock mode. Using mock user data for development.');
          // Return mock user data for development
          return {
            token: 'mock-jwt-token-' + Date.now(),
            user: {
              id: user.uid,
              email: user.email,
              displayName: user.displayName || user.email.split('@')[0],
              emailVerified: user.emailVerified || false,
              planStatus: 'free',
              role: 'user',
              privateCode: 'MOCK' + Math.random().toString(36).substring(2, 8).toUpperCase(),
              createdAt: new Date().toISOString(),
              updatedAt: new Date().toISOString()
            }
          };
        }

        // If token is expired or invalid, refresh and retry
        console.log('Token expired, refreshing and retrying...');
        const freshIdToken = await user.getIdToken(true); // Force refresh
        response = await apiClient.post('/api/auth/login-firebase', {
          idToken: freshIdToken,
          uid: user.uid,
          email: user.email,
          displayName: user.displayName,
          emailVerified: user.emailVerified
        }, {
          headers: {
            Authorization: `Bearer ${freshIdToken}`,
            'X-Override-Auth': 'true' // Flag to bypass interceptor
          }
        });
      } else {
        throw backendError;
      }
    }

    return {
      token: response.data.token,
      user: response.data.user
    };
  } catch (error) {
    // Handle errors here
    const errorCode = error.code;
    const errorMessage = error.message;
    const email = error.customData?.email;
    const credential = githubProvider.credentialFromError(error);

    throw new Error(errorMessage || 'GitHub login failed');
  }
}

// Login user with Google using Firebase client SDK
export async function loginWithGoogle() {
  try {
    const result = await signInWithPopup(auth, googleProvider);
    const user = result.user;

    // Get Firebase ID token
    const idToken = await user.getIdToken();

    // Send the Firebase ID token to backend for authentication
    let response;
    try {
      response = await apiClient.post('/api/auth/login-firebase', {
        idToken: idToken,
        uid: user.uid,
        email: user.email,
        displayName: user.displayName,
        emailVerified: user.emailVerified
      }, {
        headers: {
          Authorization: `Bearer ${idToken}`,
          'X-Override-Auth': 'true' // Flag to bypass interceptor
        }
      });
    } catch (backendError) {
      // Check if it's a 401 error
      if (backendError.response && backendError.response.status === 401) {
        const errorMessage = backendError.response.data?.message || '';

        // Check if backend is in mock mode
        if (errorMessage.includes('mock') || errorMessage.includes('Mock') ||
            errorMessage.includes('development') || errorMessage.includes('placeholder')) {
          console.warn('Backend appears to be in mock mode. Using mock user data for development.');
          // Return mock user data for development
          return {
            token: 'mock-jwt-token-' + Date.now(),
            user: {
              id: user.uid,
              email: user.email,
              displayName: user.displayName || user.email.split('@')[0],
              emailVerified: user.emailVerified || false,
              planStatus: 'free',
              role: 'user',
              privateCode: 'MOCK' + Math.random().toString(36).substring(2, 8).toUpperCase(),
              createdAt: new Date().toISOString(),
              updatedAt: new Date().toISOString()
            }
          };
        }

        // If token is expired or invalid, refresh and retry
        console.log('Token expired, refreshing and retrying...');
        const freshIdToken = await user.getIdToken(true); // Force refresh
        response = await apiClient.post('/api/auth/login-firebase', {
          idToken: freshIdToken,
          uid: user.uid,
          email: user.email,
          displayName: user.displayName,
          emailVerified: user.emailVerified
        }, {
          headers: {
            Authorization: `Bearer ${freshIdToken}`,
            'X-Override-Auth': 'true' // Flag to bypass interceptor
          }
        });
      } else {
        throw backendError;
      }
    }

    return {
      token: response.data.token,
      user: response.data.user
    };
  } catch (error) {
    // Handle errors here
    const errorCode = error.code;
    const errorMessage = error.message;
    const email = error.customData?.email;
    const credential = googleProvider.credentialFromError(error);

    throw new Error(errorMessage || 'Google login failed');
  }
}
